import React, { useEffect, useState, useCallback } from 'react';
import { workQueueAPI, followUpAPI, noteAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/timeFormat';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const CARD = [
  { key: 'new_assigned_leads', label: 'New Assigned Leads', color: 'text-blue-600' },
  { key: 'follow_ups_today', label: 'Follow-ups Today', color: 'text-green-600' },
  { key: 'overdue_follow_ups', label: 'Overdue Follow-ups', color: 'text-red-600' },
  { key: 'hot_leads', label: 'Hot Leads', color: 'text-orange-600' },
  { key: 'client_callbacks', label: 'Client Callbacks', color: 'text-teal-600' },
  { key: 'quotations_pending', label: 'Quotations Pending', color: 'text-purple-600' },
  { key: 're_enquiries', label: 'Re-Enquiries', color: 'text-cyan-600' },
];

export const WorkQueuePage = () => {
  const { user } = useAuth();
  const tf = user?.time_format || '12h';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await workQueueAPI.get(); setData(res.data); }
    catch (e) { toast.error('Failed to load work queue'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const completeFU = async (fu) => {
    try { await followUpAPI.update(fu.id, { completed: true, outcome: 'completed' }); toast.success('Follow-up completed'); load(); }
    catch (e) { toast.error('Failed to complete'); }
  };
  const callCompletedFU = async (fu) => {
    try { await followUpAPI.callCompleted(fu.id); toast.success('Call marked completed'); load(); }
    catch (e) { toast.error('Failed to log call'); }
  };
  const rescheduleFU = async (fu) => {
    const d = window.prompt('Reschedule to (YYYY-MM-DD):');
    if (!d) return;
    try { await followUpAPI.update(fu.id, { scheduled_date: new Date(`${d}T09:00:00`).toISOString() }); toast.success('Follow-up rescheduled'); load(); }
    catch (e) { toast.error('Failed to reschedule'); }
  };
  const addNoteFU = async (fu) => {
    const n = window.prompt('Add a note to this lead:');
    if (!n || !fu.lead_id) return;
    try { await noteAPI.create({ lead_id: fu.lead_id, content: n, attachments: [] }); toast.success('Note added'); }
    catch (e) { toast.error('Failed to add note'); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  if (!data) return null;
  const c = data.counts || {};

  const FollowUpList = ({ title, items }) => (
    <div className="clay-card p-4">
      <h3 className="font-medium mb-3">{title} <span className="text-muted-foreground">({items.length})</span></h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Nothing here.</p> : (
        <ul className="space-y-2">
          {items.slice(0, 15).map(f => (
            <li key={f.id} className="flex items-center justify-between gap-2 text-sm border-b border-slate-100 dark:border-slate-800 pb-2" data-testid="wq-fu-item">
              <Link to={`/leads/${f.lead_id}?tab=followup`} className="text-primary hover:underline truncate">{f.follow_up_type || 'call'} · {f.scheduled_date ? formatDateTime(f.scheduled_date, tf) : ''}</Link>
              <span className="flex gap-1 shrink-0">
                <button onClick={() => completeFU(f)} data-testid="wq-complete" className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200">Complete</button>
                <button onClick={() => callCompletedFU(f)} data-testid="wq-call-completed" className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Call Done</button>
                <button onClick={() => rescheduleFU(f)} data-testid="wq-reschedule" className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700 hover:bg-amber-200">Reschedule</button>
                <button onClick={() => addNoteFU(f)} data-testid="wq-addnote" className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200">Note</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const LeadList = ({ title, items, showMobile }) => (
    <div className="clay-card p-4">
      <h3 className="font-medium mb-3">{title} <span className="text-muted-foreground">({items.length})</span></h3>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Nothing here.</p> : (
        <ul className="space-y-2">
          {items.slice(0, 15).map(l => (
            <li key={l.id} className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2">
              <Link to={`/leads/${l.lead_id || l.id}`} className="text-primary hover:underline">{l.customer_name || l.notes || l.lead_id || l.id}</Link>
              <span className="text-muted-foreground">{showMobile ? (l.primary_mobile || '') : (l.scheduled_date ? formatDateTime(l.scheduled_date, tf) : (l.status || ''))}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="work-queue-page">
      <div>
        <h1 className="text-3xl font-outfit font-semibold">Today's Work</h1>
        <p className="text-muted-foreground mt-1">Auto-generated from your leads and follow-ups. Items update as you act on them.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {CARD.map(k => (
          <div key={k.key} className="clay-card p-4 text-center" data-testid={`wq-count-${k.key}`}>
            <div className={`text-2xl font-semibold ${k.color}`}>{c[k.key] ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LeadList title="Overdue Follow-ups" items={data.overdue_follow_ups || []} />
        <LeadList title="Follow-ups Today" items={data.follow_ups_today || []} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FollowUpList title="Overdue — Quick Actions" items={data.overdue_follow_ups || []} />
        <FollowUpList title="Today — Quick Actions" items={data.follow_ups_today || []} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LeadList title="New Assigned Leads" items={data.new_assigned_leads || []} showMobile />
        <LeadList title="Hot Leads" items={data.hot_leads || []} showMobile />
        <LeadList title="Client Callbacks" items={data.client_callbacks || []} showMobile />
        <LeadList title="Re-Enquiries" items={data.re_enquiries || []} showMobile />
      </div>
    </div>
  );
};
export default WorkQueuePage;
